import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Button,
  Chip,
  IconButton,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Link, useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Grid from "@mui/material/Grid2";
import { useDispatch } from "react-redux";
import {
  setDestinationTablesData,
  setInitialDuplicateData,
  setInterfaceData,
  setSelectedDestinationTableCreateScreen,
  setSelectedDestinationTableDuplicateScreen,
  setSelectedInterfaceCreateScreen,
  setSelectedInterfaceDuplicateScreen,
  setSort,
} from "../../../../features/uniqueDefSlice";
import { useSelector } from "react-redux";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import {
  selectUserData,
  selectUserToken,
} from "../../../../features/userSlice";
import UniqueDefinitionsFilter from "./UniqueDefinitionsFilter";
import CreateNewDefinitionDialog from "./CreateNewDefinition/CreateNewDefinitionDialog";
import { UniqueDefinitionsEndpointLogic } from "../../../../logic/DataCollection/Unifier/UniqueDefinitionsEndpointLogic";
import SimpleBackdrop from "../../../BackDrop/BackDrop";
import DuplicateDefinitionDialog from "./DuplicateDefinition/DuplicateDefinitionDialog";

interface Props {
  tableHeadings: { [key: string]: string };
  rows: any[];
  brandData?: any[];
}

const UniqueDefinitionsTable = ({
  tableHeadings,
  rows,
  brandData = [],
}: Props) => {
  const response = useSelector(selectUserData);
  const hasUnifierCreateEditUniqueDefinitions = CheckPermissions(
    response?.data?.products?.data_collection,
    "unifier",
    "create/edit_unique_definitions"
  );
  const [page, setPage] = React.useState(0);
  const [, setSelectedRows] = React.useState([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const [intDefLoading, setIntDefLoading] = React.useState(false);

  const headCells = Object.entries(tableHeadings);
  const {
    sort,
    selectedUniqueDefID,
    selectedUniqueDefInterfaceName,
    selectedUniqueDefDestinationTable,
    interfaceData,
    destinationTablesData,
    uniqueDefinitionDataAll,
    initialDuplicateData,
  } = useSelector((state: any) => state.uniqueDefinition);

  const oauthTokenHeader: any = useSelector(selectUserToken);
  const dispatch = useDispatch();
  const openCreateDialog = searchParams.get("open") === "true";
  const openDuplicateDialog = searchParams.get("openDup") === "true";
  const interfaceId = searchParams.get("interfaceId");
  const interfaceName = searchParams.get("interfaceName");
  const destinationTableId = searchParams.get("destinationTableId");
  const destinationTableName = searchParams.get("destinationTableName");

  const getBrandsForUniqueDefinition = React.useCallback(
    (uniqueDefId: number) => {
      if (!brandData || brandData.length === 0) return [];

      return brandData
        .filter((brand) => brand.raw_data_unique_definition_id === uniqueDefId)
        .map((brand) => brand.brand_name)
        .filter((brandName, index, self) => self.indexOf(brandName) === index); // Remove duplicates
    },
    [brandData]
  );
  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", newPage.toString()); // 0-indexed
    updatedParams.set("rowsPerPage", rowsPerPage.toString());
    setSearchParams(updatedParams);
  };
  const filteredData = sortedData?.filter((row: any) => {
    const selectedUniqueDefIDFilter =
      selectedUniqueDefID && selectedUniqueDefID.length !== 0
        ? selectedUniqueDefID.includes(String(row.id))
        : true;
    const selectedUniqueDefInterfaceNameFilter =
      selectedUniqueDefInterfaceName &&
      selectedUniqueDefInterfaceName.length !== 0
        ? selectedUniqueDefInterfaceName.includes(
            row.affiliate_system_interface
          )
        : true;
    const selectedUniqueDefDestinationTableFilter =
      selectedUniqueDefDestinationTable &&
      selectedUniqueDefDestinationTable.length !== 0
        ? selectedUniqueDefDestinationTable.includes(row.destination_table)
        : true;

    return (
      selectedUniqueDefIDFilter &&
      selectedUniqueDefInterfaceNameFilter &&
      selectedUniqueDefDestinationTableFilter
    );
  });
  const tableheaders = [
    { id: 0, KEY: "id", label: "ID", sortable: true },
    {
      id: 1,
      KEY: "affiliate_system_interface",
      label: "Interface Name",
      sortable: true,
    },
    {
      id: 2,
      KEY: "destination_table",
      label: "Destination Table",
      sortable: true,
    },
    {
      id: 3,
      KEY: "used_by",
      label: "Used By",
      sortable: true,
    },
  ];

  const handleCloseCreateDialog = () => {
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete("open");
    updatedParams.delete("interfaceId");
    updatedParams.delete("interfaceName");
    updatedParams.delete("destinationTableId");
    updatedParams.delete("destinationTableName");
    setSearchParams(updatedParams);
    dispatch(setSelectedInterfaceCreateScreen(null));
    dispatch(setSelectedDestinationTableCreateScreen(null));
  };
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(
      setSort({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        let aValue;
        let bValue;

        if (keytoSort === "used_by") {
          // Handle sorting for "Used By" column
          const brandsA = getBrandsForUniqueDefinition(a.id);
          const brandsB = getBrandsForUniqueDefinition(b.id);

          aValue = brandsA.length > 0 ? brandsA[0] : "";
          bValue = brandsB.length > 0 ? brandsB[0] : "";
        } else {
          aValue = a[keytoSort];
          bValue = b[keytoSort];
        }

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort, getBrandsForUniqueDefinition]
  );

  const sortedRows = getSortedData(filteredData);
  const paginatedRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = +event.target.value;
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", "0"); // Reset page to 0
    updatedParams.set("rowsPerPage", newRowsPerPage.toString());
    setSearchParams(updatedParams);
  };

  const handleCloseDuplicateDialog = () => {
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete("openDup");
    updatedParams.delete("interfaceId");
    updatedParams.delete("interfaceName");
    updatedParams.delete("destinationTableId");
    updatedParams.delete("destinationTableName");
    setSearchParams(updatedParams);
    dispatch(setSelectedInterfaceDuplicateScreen(null));
    dispatch(setSelectedDestinationTableDuplicateScreen(null));
  };

  React.useEffect(() => {
    const pageParam = parseInt(searchParams.get("page") || "0", 10);
    const rowsParam = parseInt(searchParams.get("rowsPerPage") || "10", 10);

    const validPage = !isNaN(pageParam) && pageParam >= 0 ? pageParam : 0;
    const validRowsPerPage =
      !isNaN(rowsParam) && [10, 25, 100].includes(rowsParam) ? rowsParam : 10;

    setPage(validPage);
    setRowsPerPage(validRowsPerPage);

    // If URL values were invalid, update them to valid defaults
    if (pageParam !== validPage || rowsParam !== validRowsPerPage) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("page", validPage.toString());
      updatedParams.set("rowsPerPage", validRowsPerPage.toString());
      setSearchParams(updatedParams, { replace: true });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const totalPages = Math.ceil(rows.length / rowsPerPage);

    if (page >= totalPages && totalPages > 0) {
      setPage(0);

      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("page", "0");
      setSearchParams(updatedParams, { replace: true });
    }
  }, [rows, page, rowsPerPage, searchParams, setSearchParams]);

  React.useEffect(() => {
    setSelectedRows([]);
    // eslint-disable-next-line
  }, []);
  React.useEffect(() => {
    const sortedRows = getSortedData(rows || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [rows, getSortedData]);

  React.useEffect(() => {
    if (page * rowsPerPage >= filteredData.length && page !== 0) {
      setPage(0);
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("page", "0");
      setSearchParams(updatedParams);
    }
    // eslint-disable-next-line
  }, [
    filteredData.length,
    rowsPerPage,
    selectedUniqueDefID,
    selectedUniqueDefInterfaceName,
    selectedUniqueDefDestinationTable,
  ]);

  React.useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl); // Ensure it defaults correctly
  }, [searchParams]);

  const isDataMissing = () => {
    const noInterfaceData =
      !interfaceData ||
      (Array.isArray(interfaceData) && interfaceData.length === 0);
    const noDestinationData =
      !destinationTablesData ||
      (Array.isArray(destinationTablesData) &&
        destinationTablesData.length === 0);
    return noInterfaceData || noDestinationData;
  };
  const fetchIntDefData = async () => {
    if (intDefLoading) return;

    setIntDefLoading(true);
    try {
      await Promise.all([
        UniqueDefinitionsEndpointLogic.getInterface(
          oauthTokenHeader,
          setInterfaceData
        ),
        UniqueDefinitionsEndpointLogic.getDestinationTables(
          oauthTokenHeader,
          setDestinationTablesData
        ),
      ]);
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("open", "true");
      setSearchParams(updatedParams);
    } finally {
      setIntDefLoading(false);
    }
  };

  const handleDuplicateClick = async (duplicateId: number) => {
    if (intDefLoading) return;
    setIntDefLoading(true);
    try {
      // Ensure interfaces + destination tables are loaded first
      await Promise.all([
        UniqueDefinitionsEndpointLogic.getInterface(
          oauthTokenHeader,
          setInterfaceData
        ),
        UniqueDefinitionsEndpointLogic.getDestinationTables(
          oauthTokenHeader,
          setDestinationTablesData
        ),
        UniqueDefinitionsEndpointLogic.getAllUnifierUniqueDefinitions(
          oauthTokenHeader
        ),
      ]);

      // Find the matched unique definition
      const matched = Array.isArray(uniqueDefinitionDataAll)
        ? uniqueDefinitionDataAll.find(
            (d: any) => Number(d?.id) === Number(duplicateId)
          )
        : null;

      dispatch(setInitialDuplicateData(matched ?? null));

      if (matched) {
        // Wait a bit to ensure the data is loaded in Redux state
        setTimeout(() => {
          // Find the interface ID based on the interface name
          let interfaceId = null;
          let destinationTableId = null;

          // Get the current interface and destination table data from Redux
          const currentInterfaceData = interfaceData || [];
          const currentDestinationTablesData = destinationTablesData || [];

          // Find interface ID
          const matchedInterface = currentInterfaceData.find(
            (item: any) =>
              item.affiliate_system_interface_name ===
              matched.affiliate_system_interface
          );
          if (matchedInterface) {
            interfaceId = matchedInterface.id;
            dispatch(setSelectedInterfaceDuplicateScreen(matchedInterface));
          }

          // Find destination table ID
          const matchedDestinationTable = currentDestinationTablesData.find(
            (item: any) => item.destination_table === matched.destination_table
          );
          if (matchedDestinationTable) {
            destinationTableId = matchedDestinationTable.id;
            dispatch(
              setSelectedDestinationTableDuplicateScreen(
                matchedDestinationTable
              )
            );
          }

          // Set URL parameters with the found IDs and names
          const updatedParams = new URLSearchParams(searchParams);
          updatedParams.set("openDup", "true");

          if (interfaceId && matched.affiliate_system_interface) {
            updatedParams.set("interfaceId", interfaceId.toString());
            updatedParams.set(
              "interfaceName",
              matched.affiliate_system_interface
            );
          }

          if (destinationTableId && matched.destination_table) {
            updatedParams.set(
              "destinationTableId",
              destinationTableId.toString()
            );
            updatedParams.set(
              "destinationTableName",
              matched.destination_table
            );
          }

          setSearchParams(updatedParams);
        }, 100); // Small delay to ensure Redux state is updated
      } else {
        // If no match found, just open the dialog
        const updatedParams = new URLSearchParams(searchParams);
        updatedParams.set("openDup", "true");
        setSearchParams(updatedParams);
      }
    } catch (err) {
      console.error("Error fetching data for duplication:", err);
      // optionally show a toast / snackbar
    } finally {
      setIntDefLoading(false);
    }
  };

  React.useEffect(() => {
    const restoreDuplicateData = async () => {
      if (
        openDuplicateDialog &&
        (!initialDuplicateData ||
          Object.keys(initialDuplicateData).length === 0)
      ) {
        try {
          // First, try to fetch all unique definitions
          await UniqueDefinitionsEndpointLogic.getAllUnifierUniqueDefinitions(
            oauthTokenHeader
          );

          let matched = null;
          const originalId = searchParams.get("originalId");

          // Try to find by original ID first (most reliable)
          if (originalId) {
            matched = Array.isArray(uniqueDefinitionDataAll)
              ? uniqueDefinitionDataAll.find(
                  (d: any) => Number(d?.id) === Number(originalId)
                )
              : null;
          }

          // Fallback: Find by interface and destination table names
          if (!matched && interfaceName && destinationTableName) {
            matched = Array.isArray(uniqueDefinitionDataAll)
              ? uniqueDefinitionDataAll.find(
                  (d: any) =>
                    d.affiliate_system_interface === interfaceName &&
                    d.destination_table === destinationTableName
                )
              : null;
          }

          if (matched) {
            dispatch(setInitialDuplicateData(matched));
          }
        } catch (error) {
          console.error("Error restoring duplicate data:", error);
        }
      }
    };

    restoreDuplicateData();
  }, [
    openDuplicateDialog,
    interfaceName,
    destinationTableName,
    initialDuplicateData,
    uniqueDefinitionDataAll,
    oauthTokenHeader,
    dispatch,
    searchParams,
  ]);
  React.useEffect(() => {
    if (
      (openCreateDialog || openDuplicateDialog) &&
      !intDefLoading &&
      isDataMissing()
    ) {
      const fetchData = async () => {
        setIntDefLoading(true);
        try {
          await Promise.all([
            UniqueDefinitionsEndpointLogic.getInterface(
              oauthTokenHeader,
              setInterfaceData
            ),
            UniqueDefinitionsEndpointLogic.getDestinationTables(
              oauthTokenHeader,
              setDestinationTablesData
            ),
          ]);
        } finally {
          setIntDefLoading(false);
        }
      };
      fetchData();
    }
    //eslint-disable-next-line
  }, [
    openCreateDialog,
    openDuplicateDialog,
    interfaceData,
    destinationTablesData,
    intDefLoading,
    oauthTokenHeader,
  ]);
  // Restore selections from URL parameters
  React.useEffect(() => {
    if (
      (openCreateDialog || openDuplicateDialog) &&
      interfaceData &&
      destinationTablesData
    ) {
      // Restore interface selection
      if (interfaceId && interfaceName) {
        const selectedInterface = interfaceData.find(
          (item: any) => item.id.toString() === interfaceId
        );
        if (selectedInterface && openCreateDialog) {
          dispatch(setSelectedInterfaceCreateScreen(selectedInterface));
        } else if (selectedInterface && openDuplicateDialog) {
          dispatch(setSelectedInterfaceDuplicateScreen(selectedInterface));
        }
      }

      // Restore destination table selection
      if (destinationTableId && destinationTableName) {
        const selectedDestTable = destinationTablesData.find(
          (item: any) => item.id.toString() === destinationTableId
        );
        if (selectedDestTable && openCreateDialog) {
          dispatch(setSelectedDestinationTableCreateScreen(selectedDestTable));
        } else if (selectedDestTable && openDuplicateDialog) {
          dispatch(
            setSelectedDestinationTableDuplicateScreen(selectedDestTable)
          );
        }
      }
    }
  }, [
    openCreateDialog,
    interfaceData,
    destinationTablesData,
    interfaceId,
    interfaceName,
    destinationTableId,
    destinationTableName,
    dispatch,
    openDuplicateDialog,
  ]);

  return (
    <>
      <Paper sx={{ mt: 0.5 }}>
        <Box sx={{ padding: "8px 16px" }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Grid size={6}>
              <Typography variant="h3" sx={{ fontSize: "24px" }}>
                Unique Definitions
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "14px" }}>
                View, create and edit the Unique Definitions used for extracting
                the correct term for unifying the data from raw tables to the
                destination tables.
              </Typography>
            </Grid>
            <Grid size={8}>
              <UniqueDefinitionsFilter />
            </Grid>
            <Grid
              size={4}
              container
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
            >
              {hasUnifierCreateEditUniqueDefinitions ? (
                <Button
                  data-tracker="493"
                  variant="outlined"
                  color="secondary"
                  sx={{ whiteSpace: "nowrap" }}
                  onClick={fetchIntDefData}
                >
                  Create new Definition{" "}
                  <AddIcon
                    sx={{ width: "20px", ml: "5px" }}
                    color="secondary"
                  />
                </Button>
              ) : (
                <Button variant="contained" sx={{ whiteSpace: "nowrap" }}>
                  Create new Definition{" "}
                  <AddIcon sx={{ width: "20px", ml: "5px" }} />
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
        <TableContainer className="" component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {tableheaders.map((header: any) => (
                  <TableCell key={header.id} align="left">
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={
                        sort.keytoSort === header.KEY ? sort.direction : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{ width: "200px", textAlign: "center" }}
                >
                  <TableSortLabel>Quick Actions</TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows?.map((row) => {
                const relatedBrands = getBrandsForUniqueDefinition(row.id);
                return (
                  <React.Fragment key={row.cron_id}>
                    <TableRow
                      sx={{
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      {headCells.map(([key], cellIndex) => (
                        <TableCell key={cellIndex} align="left">
                          {key === "id" ? (
                            <> {row[key] || "N/A"} </>
                          ) : key === "destination_table" ? (
                            <> {row[key] ? row[key] : "N/A"} </>
                          ) : key === "affiliate_system_interface" ? (
                            <> {row[key] ? row[key] : "N/A"} </>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                      ))}
                      <TableCell
                        align="left"
                        sx={{ minWidth: "200px", maxWidth: "300px" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            overflowX: "auto",
                            overflowY: "hidden",
                            paddingY: 0.5,
                            paddingBottom: 1,
                            // Custom scrollbar styling
                            "&::-webkit-scrollbar": {
                              height: "7px",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "#f1f1f1",
                              borderRadius: "3px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#d7d7d7ff",
                              borderRadius: "3px",
                              "&:hover": {
                                backgroundColor: "#a8a8a8",
                              },
                            },
                          }}
                        >
                          {relatedBrands.length > 0 ? (
                            relatedBrands.map((brand, index) => (
                              <Chip
                                key={index}
                                label={brand}
                                sx={{
                                  backgroundColor: "#7B1FA2",
                                  color: "#ffffff",
                                  height: "26px",
                                  minWidth: "fit-content",
                                  flexShrink: 0, // Prevent chips from shrinking
                                  "& .MuiChip-label": {
                                    whiteSpace: "nowrap",
                                  },
                                }}
                              />
                            ))
                          ) : (
                            <Chip
                              label="Not used"
                              sx={{
                                backgroundColor: "#E0E0E0",
                                color: "#666666",
                                height: "26px",
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        {hasUnifierCreateEditUniqueDefinitions ? (
                          <Tooltip title="Edit" placement="top">
                            <IconButton
                              sx={{ width: "40px", height: "40px" }}
                              data-tracker="384"
                            >
                              <Link
                                to={`/data_collection/unifier/unique_definitions/edit_definition?id=${row.id}&interfaceName=${row.affiliate_system_interface}&destinationTableName=${row.destination_table}`}
                              >
                                <EditIcon color="secondary" fontSize="small" />
                              </Link>
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Edit" placement="top">
                            <IconButton
                              sx={{ width: "40px", height: "40px" }}
                              disabled
                            >
                              <EditIcon color="disabled" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasUnifierCreateEditUniqueDefinitions ? (
                          <Tooltip title="Duplicate" placement="top">
                            <IconButton
                              sx={{ width: "40px", height: "40px" }}
                              data-tracker="494"
                              onClick={() => handleDuplicateClick(row.id)}
                            >
                              <ContentCopyIcon
                                color="secondary"
                                fontSize="small"
                              />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Duplicate" placement="top">
                            <IconButton
                              sx={{ width: "40px", height: "40px" }}
                              disabled
                            >
                              <ContentCopyIcon
                                color="disabled"
                                fontSize="small"
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={sortedRows?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                  {
                    display: "none",
                  },
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-toolbar": {
                  paddingLeft: "16px",
                },
              }}
            />
            <Pagination
              count={Math.ceil(filteredData?.length / rowsPerPage)}
              page={page + 1}
              showFirstButton
              showLastButton
              className="pagination-button"
              color="secondary"
              size="small"
              sx={{
                ".css-wjh20t-MuiPagination-ul": {
                  justifyContent: "center",
                },
              }}
              onChange={(_, value) => handleChangePage(null, value - 1)}
            />
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={sortedRows?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              classes={{
                displayedRows: "hidden-displayed-rows", // Apply the custom class
              }}
              sx={{
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-input": {
                  marginRight: "15px",
                },
              }}
            />
          </Stack>
        </TableContainer>
      </Paper>
      {intDefLoading && <SimpleBackdrop />}
      <CreateNewDefinitionDialog
        open={openCreateDialog}
        onClose={handleCloseCreateDialog}
      />
      <DuplicateDefinitionDialog
        open={openDuplicateDialog}
        onClose={handleCloseDuplicateDialog}
      />
    </>
  );
};

export default UniqueDefinitionsTable;
